import { useMemo, useCallback, useState, useEffect } from 'react';
import classNames from 'classnames';
import { message, Alert } from '@hose/eui';
import { ToolComponentCtx } from '@hose/chat-app';
import { Consumption, ConsumptionCategory, OriginalVoucher } from './types';
import './index.less';
import { deleteConsumptionById, updateChatHistory, sendMessage, makeMessageKey } from '../../utils/postmate';
import { ConsumptionCard } from './ConsumptionCard';
import { updateMsg, MsgBody, getParamFromUrl, formatAmount} from '../../utils/tools';
import { MoneyObject } from '../../types/dataMatched'

interface DataSource {
    id?: string | number;
    title?: string;
    consumptionCategory?: ConsumptionCategory;
    consumptionType?: string;
    consumptionAmount?: MoneyObject | string | number;
    amountText?: string;
    consumptionDescription?: string;
    invoices?: string[];
    originalVouchers?: OriginalVoucher[];
    invoiceCount?: number;
    attachmentCount?: number;
    hasInvoice?: boolean;
    currency?: string;
}

export type MyConsumptionsProps = ToolComponentCtx & {
    result?: any;
    items?: Consumption[];
    className?: string;
    footerText?: string;
    onRemove?: (id: string | number) => void;
    message?: MsgBody;
};

/**
 * 费用摘要卡片列表，贴近 Figma 设计展示 AI 提取的消费信息。
 * props 兼容 ToolComponentCtx，直接消费 AI 返回的 result。
 *
 * 示例：
 * ```tsx
 * <MyConsumptions
 *   state="result"
 *   result={[
 *     { id: '1', title: '差旅费-打车', amount: 'CNY 100.00', remark: '备注内容', invoiceCount: 1, attachmentCount: 2, iconName: 'car' },
 *     { id: '2', title: '差旅费-住宿', amount: 'CNY 1620.00', remark: '备注内容', invoiceCount: 1, attachmentCount: 2, iconName: 'building' }
 *   ]}
 * />
 * ```
 */
export default function MyConsumptions({
    toolCallId,
    state,
    result,
    items,
    className,
    footerText = '内容由AI生成',
    message: part,
    onRemove
}: MyConsumptionsProps) {
    const mapToDataSource = useCallback((list: Consumption[]): DataSource[] => {
        // 辅助函数：获取消费标题
        const getConsumptionTitle = (item: Consumption): string => {
            return item.consumptionCategory && item.consumptionType
                ? `${item.consumptionCategory}-${item.consumptionType}`
                : item.consumptionType || item.consumptionCategory || '其他费用'
        };

        return list.map((item) => ({
            id: item?.id,
            consumptionCategory: item.consumptionCategory,
            consumptionType: item.consumptionType,
            title: getConsumptionTitle(item),
            consumptionAmount: item.consumptionAmount,
            amountText: formatAmount(item.consumptionAmount),
            consumptionDescription: item?.consumptionDescription,
            invoices: item?.invoices,
            originalVouchers: item?.originalVouchers,
            invoiceCount: Array.isArray(item?.invoices) ? item.invoices.length : 0,
            hasInvoice: Array.isArray(item?.invoices) && item.invoices?.length > 0,
            attachmentCount: Array.isArray(item?.originalVouchers) ? item.originalVouchers.length : 0,
        }));
    }, []);

    const dataSource = useMemo<DataSource[]>(() => {
        if (Array.isArray(items) && items.length > 0) return mapToDataSource(items);
        if (!result) return [];

        const pickArray = (): Consumption[] => {
            if (Array.isArray(result)) return result as Consumption[];
            if (typeof result === 'string') {
                try {
                    const parsed = JSON.parse(result);
                    if (Array.isArray(parsed)) return parsed as Consumption[];
                } catch {
                    return [];
                }
            }
            return [];
        };

        const list = pickArray();
        return mapToDataSource(list);
    }, [items, result, mapToDataSource]);

    const sessionId = getParamFromUrl('sessionId') || 'default-session';
    const removedKey = `my-consumptions-removed-${sessionId}`;

    // 记录已删除的项的 ID 列表（持久化到 sessionStorage，防止刷新或继续对话时重新出现）
    const [removedIds, setRemovedIds] = useState<Set<string | number>>(() => {
        try {
            const cached = sessionStorage.getItem(removedKey);
            if (cached) {
                const arr = JSON.parse(cached);
                if (Array.isArray(arr)) {
                    return new Set(arr);
                }
            }
        } catch (e) {
            console.warn('读取已删除消费缓存失败', e);
        }
        return new Set();
    });

    useEffect(() => {
        try {
            sessionStorage.setItem(removedKey, JSON.stringify(Array.from(removedIds)));
        } catch (e) {
            console.warn('写入已删除消费缓存失败', e);
        }
    }, [removedIds, removedKey]);

    const defaultRemove = async (id?: string | number) => {
        if (id === undefined || id === null) {
            message.warning('记录不存在，可能已被删除')
            return;
        }
        try {
            const resp = await deleteConsumptionById({ id })
            if (resp.success) {
                message.success(resp.message)
                const newResult = result.filter((item: Consumption) => item.id !== id)
                if (part) {
                    const newMsgBody = updateMsg(part, toolCallId, newResult)
                    const chatId = getParamFromUrl('sessionId')
                    if (chatId) {
                        try {
                            await updateChatHistory(chatId, part.id, newMsgBody)
                            console.log('聊天历史更新成功')
                        } catch (err) {
                            console.error('更新聊天历史失败:', err)
                        }
                    }
                }
                // 删除成功后，将该 id 加入已删除列表
                setRemovedIds(prev => new Set(prev).add(id));
            }
        } catch (error) {
            console.error('删除请求异常', error);
        }
    };

    // 过滤掉已删除的项
    const filteredDataSource = useMemo(() => {
        return dataSource.filter(item => item.id === undefined || !removedIds.has(item.id));
    }, [dataSource, removedIds]);

    // 数据渲染完成后，通知主端消费 ID 列表
    useEffect(() => {
        if (!filteredDataSource || filteredDataSource.length === 0) return;
        const ids = filteredDataSource.map(item => item.id).filter(Boolean);
        if (ids.length === 0) return;
        sendMessage(makeMessageKey('ai:consumption:rendered'), {ids});
    }, [filteredDataSource]);

    if (state !== 'result') {
        return null;
    }

    if (filteredDataSource.length === 0) {
        return <div style={{
            maxWidth: '448px',
            marginTop: '10px'
        }}>
            <Alert message="生成的相关消费明细数据已被用户删除" type="warning" />
        </div>
    }

    return (
        <div className={classNames('my-consumptions', className)}>
            {filteredDataSource.map((item, idx) => (
                <ConsumptionCard
                    key={item.id ?? idx}
                    {...item}
                    onDelete={() => {
                        if (onRemove) {
                            onRemove(item.id ?? idx);
                        } else {
                            defaultRemove(item.id ?? idx);
                        }
                    }}
                />
            ))}
            <div className="my-consumptions__footer">{footerText}</div>
        </div>
    );
}